import { useMemo } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { GaugeChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { LoadingSpinner, StatusBadge } from "@evos/components";
import type { ChargerStatus } from "../../types";

import "./chargerLoadChart.css";

type ChargerLoadChartProps = {
  currentUsed: number;
  currentOffered: number;
  maxCapacity: number;
  isLoading: boolean;
  colours: [string, string, string];
  title: string;
  chargerStatus?: ChargerStatus;
};

export default function ChargerLoadChart({
  currentUsed,
  currentOffered,
  maxCapacity,
  isLoading,
  colours,
  title,
  chargerStatus,
}: ChargerLoadChartProps) {
  const chargerLoadChartOptions = useMemo(() => {
    const cappedUsed = Math.min(currentUsed, maxCapacity);
    const cappedOffered = Math.min(currentOffered, maxCapacity);
    const chartCurrentUsed = cappedUsed % 1 !== 0 ? Number(cappedUsed.toFixed(2)) : cappedUsed;
    const chartCurrentOffered = cappedOffered % 1 !== 0 ? Number(cappedOffered.toFixed(2)) : cappedOffered;
    return {
      title: {
        show: !isLoading,
        text: `${chartCurrentUsed} A`,
        subtext: "Used",
        left: "center",
        top: "center",
        textStyle: { fontSize: 23, color: "#000000" },
        subtextStyle: { fontSize: 14, fontWeight: "bold", color: "#8A92A6" },
        itemGap: 0,
      },
      tooltip: {
        trigger: "item",
        formatter: `
          <span><span class="current-used-circle"></span> Current used: ${chartCurrentUsed} A</span><br/>
          <span><span class="current-offered-circle"></span> Current offered: ${chartCurrentOffered} A</span><br/>
          <span><span class="max-capacity-circle"></span> Capacity: ${maxCapacity} A</span>`,
      },
      series: [
        {
          type: "gauge",
          startAngle: 90,
          endAngle: -270,
          max: maxCapacity + 0.1,
          radius: "85%",
          top: 0,
          bottom: 0,
          pointer: { show: false },
          progress: { show: true, overlap: true, roundCap: true, clip: false, width: 18 },
          axisLine: { lineStyle: { width: 18 } },
          splitLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          title: { show: false },
          detail: { show: false },
          data:
            cappedUsed !== 0
              ? [
                { name: "Current used", value: cappedUsed, itemStyle: { color: colours[0] } },
                { name: "Current offered", value: cappedOffered, itemStyle: { color: cappedOffered > cappedUsed ? colours[1] : "transparent" } },
                ...(cappedOffered < maxCapacity ? [{ name: "Capacity", value: maxCapacity, itemStyle: { color: colours[2] } }] : []),
              ]
              : [{ name: "Capacity", value: maxCapacity, itemStyle: { color: colours[2] } }],
        },
      ],
    };
  }, [currentUsed, currentOffered, maxCapacity, isLoading, colours]);

  echarts.use([TitleComponent, TooltipComponent, LegendComponent, GridComponent, DatasetComponent, GaugeChart, SVGRenderer]);

  return (
    <div id="ChargerLoadChart" style={{ position: "relative" }}>
      {isLoading && (
        <div style={{ position: "absolute", top: "35%", left: "47%" }}>
          <LoadingSpinner />
        </div>
      )}
      <ReactEChartsCore
        echarts={echarts}
        option={chargerLoadChartOptions}
        style={{ width: "210px", height: "210px" }}
        opts={{ renderer: "svg" }}
      />
      <span className="d-flex flex-column flex-wrap row-gap-2 justify-content-center align-items-center">
        <span className="fw-semibold text-center" style={{ maxWidth: "200px" }}>{title}</span>
        {chargerStatus != null && <StatusBadge status={chargerStatus} size="sm" />}
      </span>
    </div>
  );
}
